import { apiClient } from './api';
import type { AuthService, LoginCredentials, AuthResponse, User } from '../types';

class AuthServiceImpl implements AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // FastAPI expects form data for OAuth2 token endpoint
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await apiClient.post<AuthResponse>('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Store the token in the API client
      apiClient.setAuthToken(response.access_token);

      return response;
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      return await apiClient.get<User>('/auth/me');
    } catch (error) {
      throw error;
    }
  }

  logout(): void {
    // Clear token from API client and storage
    apiClient.clearAuthToken();

    // Emit logout event for other components to listen
    window.dispatchEvent(new CustomEvent('auth-logout'));
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      // If your FastAPI backend supports refresh tokens, implement here
      // For now, we'll just return the current token info
      await this.getCurrentUser(); // Validate current token

      // This is a placeholder - implement actual refresh logic if needed
      return {
        access_token: localStorage.getItem('auth_token') || '',
        token_type: 'bearer',
      };
    } catch (error) {
      // If refresh fails, logout the user
      this.logout();
      throw error;
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    return !!token;
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
    apiClient.setAuthToken(token);
  }

  clearToken(): void {
    localStorage.removeItem('auth_token');
    apiClient.clearAuthToken();
  }
}

export const authService = new AuthServiceImpl();
export default authService;